/**
 * Shared Figure Finder for collection and search result sections.
 *
 * Collections use Shopify's native /collections/handle/tag+tag paths. Search
 * pages use Shopify's built-in tag: query syntax. Neither mode depends on the
 * Search & Discovery product-tag filter or its tag-option limit.
 */
(() => {
  if (customElements.get('catalog-figure-finder')) return;
  class CatalogFigureFinder extends HTMLElement {
    constructor() {
      super();
      this.storageKey = 'reaper-eu-figure-finder-collapsed';
      this.context = this.dataset.context || 'collection';
      this.compatibilityCache = new Map();
      this.compatibilityKeyMap = new Map();
      this.compatibilityIndex = null;
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onOptionSearch = this.onOptionSearch.bind(this);
      this.onUrlChange = this.onUrlChange.bind(this);
      this.desktopMedia = window.matchMedia('(min-width: 1200px)');
      this.onViewportChange = () => {
        if (this.desktopMedia.matches) this.setMobileOpen(false);
        this.syncSidebarAccessibility();
      };
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('input', this.onOptionSearch);
      window.addEventListener('popstate', this.onUrlChange);
      window.addEventListener('catalog-figure-finder:urlchange', this.onUrlChange);
      this.desktopMedia.addEventListener('change', this.onViewportChange);
      this.onViewportChange();

      this.loadCompatibilityIndex();
      this.restoreCollapsedState();
      this.syncFromUrl(new URL(window.location.href));
      this.ensureCompatibilityReady();

      const grid = document.getElementById('ProductGridContainer');
      if (grid) {
        this.gridObserver = new MutationObserver(() => {
          this.classList.remove('is-loading');
          this.syncFromUrl(new URL(window.location.href));
        });
        this.gridObserver.observe(grid, { childList: true });
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('input', this.onOptionSearch);
      window.removeEventListener('popstate', this.onUrlChange);
      window.removeEventListener('catalog-figure-finder:urlchange', this.onUrlChange);
      this.desktopMedia.removeEventListener('change', this.onViewportChange);
      this.gridObserver?.disconnect();
      document.body.classList.remove('catalog-figure-finder-open');
    }

    onClick(event) {
      const collapseToggle = event.target.closest('[data-cff-collapse-toggle]');
      if (collapseToggle) {
        event.preventDefault();
        this.setCollapsed(!this.classList.contains('is-collapsed'), true);
        return;
      }

      const mobileToggle = event.target.closest('[data-cff-mobile-toggle]');
      if (mobileToggle) {
        event.preventDefault();
        this.setMobileOpen(!this.classList.contains('is-mobile-open'));
        return;
      }

      if (event.target.closest('[data-cff-mobile-close]')) {
        event.preventDefault();
        this.setMobileOpen(false);
        return;
      }

      const tagLink = event.target.closest('[data-cff-tag]');
      if (tagLink) {
        event.preventDefault();
        const currentUrl = new URL(this.currentUrl?.href || window.location.href);
        const activeValues = this.currentActiveValues
          ? new Set(this.currentActiveValues)
          : new Set(this.activeValues(currentUrl));
        const selected = activeValues.has(this.normalizeTagValue(tagLink.dataset.tagValue));

        if (!selected && this.isTagUnavailable(tagLink, activeValues)) {
          this.announce(
            this.hasCompleteCompatibilityIndex()
              ? 'That combination has no matching miniatures.'
              : 'Figure Finder is still checking available combinations.',
          );
          return;
        }
        this.toggleTag(tagLink, event);
        return;
      }

      const clearGroup = event.target.closest('[data-cff-clear-group]');
      if (clearGroup) {
        event.preventDefault();
        const group = clearGroup.closest('[data-cff-group]');
        this.clearTags(group ? this.groupValues(group) : [], event);
        return;
      }

      if (event.target.closest('[data-cff-clear-all]')) {
        event.preventDefault();
        this.clearTags(this.allConfiguredValues(), event);
      }
    }

    onOptionSearch(event) {
      if (!event.target.matches('[data-cff-option-search]')) return;
      const group = event.target.closest('[data-cff-group]');
      const query = event.target.value.trim().toLocaleLowerCase();
      let visibleCount = 0;
      group.querySelectorAll('[data-cff-tag]').forEach((link) => {
        const matches = link.textContent.toLocaleLowerCase().includes(query);
        link.closest('li').hidden = !matches;
        if (matches) visibleCount += 1;
      });
      const message = group.querySelector('[data-cff-no-options]');
      if (message) message.hidden = visibleCount > 0;
    }

    onKeydown(event) {
      if (event.key === 'Tab' && this.classList.contains('is-mobile-open')) {
        const sidebar = this.querySelector('[data-cff-sidebar]');
        const controls = Array.from(sidebar.querySelectorAll('summary, a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex]'))
          .filter((element) => {
            const closedGroup = element.closest('details:not([open])');
            return element.tabIndex >= 0 && element.getClientRects().length > 0
              && getComputedStyle(element).visibility !== 'hidden' && !element.closest('[inert]')
              && (!closedGroup || closedGroup.querySelector('summary') === element);
          });
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (first && ((event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last))) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
      }
      // These links retain their URLs as a no-script fallback, but act as toggles.
      const option = event.target.closest('[data-cff-tag]');
      if (option && event.key === ' ') {
        event.preventDefault();
        if (!event.repeat) option.click();
        return;
      }
      if (event.key === 'Escape' && this.classList.contains('is-mobile-open')) {
        event.preventDefault();
        event.stopPropagation();
        this.setMobileOpen(false);
      }
    }

    onUrlChange(event) {
      let url = new URL(window.location.href);
      if (event.detail?.url) {
        url = new URL(event.detail.url, window.location.origin);
      } else if (typeof event.detail?.searchParams === 'string') {
        url.search = event.detail.searchParams ? `?${event.detail.searchParams}` : '';
      }
      this.syncFromUrl(url);
    }

    ensureCompatibilityReady() {
      // The index is embedded by the sidebar snippet; there is no asynchronous loader.
      this.loadCompatibilityIndex();
      const ready = this.hasCompleteCompatibilityIndex();
      this.setCompatibilityStatus(ready ? 'ready' : 'error');
      if (!ready) {
        this.announce('Figure Finder combination guidance is temporarily unavailable.');
      }
    }

    normalizeTagValue(value) {
      return String(value ?? '')
        .normalize('NFKC')
        .trim();
    }

    tagKey(value) {
      return this.normalizeTagValue(value).toLocaleLowerCase('en-US');
    }

    loadCompatibilityIndex() {
      if (this.compatibilityIndex?.tags && this.compatibilityKeyMap.size > 0) {
        return this.hasCompleteCompatibilityIndex();
      }

      let candidate = null;
      const embedded = this.querySelector('[data-cff-compatibility]');

      if (embedded) {
        try {
          candidate = JSON.parse(embedded.textContent);
        } catch (_error) {
          candidate = null;
        }
      }

      if (!candidate?.tags || typeof candidate.tags !== 'object') return false;
      if (Number(candidate.tagCount) !== Object.keys(candidate.tags).length) return false;

      if (this.compatibilityIndex !== candidate) {
        this.compatibilityIndex = candidate;
        this.compatibilityCache.clear();
        this.compatibilityKeyMap = new Map(
          Object.entries(candidate.tags).map(([tag, encoded]) => [this.tagKey(tag), encoded]),
        );
      }

      return this.hasCompleteCompatibilityIndex();
    }

    hasCompleteCompatibilityIndex() {
      if (!this.compatibilityIndex?.tags || this.compatibilityKeyMap.size === 0) return false;
      return this.allConfiguredValues().every((value) => {
        const encoded = this.compatibilityKeyMap.get(this.tagKey(value));
        return typeof encoded === 'string' && encoded.length > 0;
      });
    }

    setCompatibilityStatus(state) {
      const message = this.querySelector('[data-cff-compatibility-message]');
      if (!message) return;

      if (state === 'ready') {
        message.hidden = true;
        message.textContent = '';
        return;
      }

      message.hidden = false;
      message.textContent = 'Figure Finder is temporarily unavailable. Please refresh the page.';
    }

    restoreCollapsedState() {
      let collapsed = this.dataset.collapsedDefault === 'true';
      try {
        const saved = window.localStorage.getItem(this.storageKey);
        if (saved !== null) collapsed = saved === 'true';
      } catch (_error) {
        // Storage can be unavailable in privacy modes; the theme default remains.
      }
      this.setCollapsed(collapsed, false);
    }

    setCollapsed(collapsed, remember) {
      this.classList.toggle('is-collapsed', collapsed);
      this.querySelectorAll('[data-cff-collapse-toggle]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', String(!collapsed));
        toggle.setAttribute('aria-label', collapsed ? 'Expand Figure Finder' : 'Collapse Figure Finder');
        toggle.setAttribute('title', collapsed ? 'Expand Figure Finder' : 'Collapse Figure Finder');
      });

      if (remember && this.desktopMedia.matches) {
        this.querySelector(collapsed ? '.catalog-figure-finder__collapsed-trigger' : '.catalog-figure-finder__collapse-toggle')?.focus();
      }

      if (remember) {
        try {
          window.localStorage.setItem(this.storageKey, String(collapsed));
        } catch (_error) {
          // A remembered state is optional; no customer action should fail here.
        }
      }
    }

    syncSidebarAccessibility() {
      const sidebar = this.querySelector('[data-cff-sidebar]');
      if (!sidebar) return;
      const mobile = !this.desktopMedia.matches;
      const open = mobile && this.classList.contains('is-mobile-open');
      const hidden = mobile && !open;
      // Move focus before excluding a panel which just moved off-screen.
      if (hidden && sidebar.contains(document.activeElement)) {
        this.querySelector('[data-cff-mobile-toggle]')?.focus();
      }
      sidebar.inert = hidden;
      if (hidden) sidebar.setAttribute('aria-hidden', 'true');
      else sidebar.removeAttribute('aria-hidden');
      if (open) {
        sidebar.setAttribute('role', 'dialog');
        sidebar.setAttribute('aria-modal', 'true');
      } else {
        sidebar.removeAttribute('role');
        sidebar.removeAttribute('aria-modal');
      }
    }

    setMobileOpen(open) {
      const wasOpen = this.classList.contains('is-mobile-open');
      const sidebar = this.querySelector('[data-cff-sidebar]');
      open = Boolean(open && !this.desktopMedia.matches);
      this.classList.toggle('is-mobile-open', open);
      document.body.classList.toggle('catalog-figure-finder-open', open);
      const toggle = this.querySelector('[data-cff-mobile-toggle]');
      toggle?.setAttribute('aria-expanded', String(open));
      if (!open && wasOpen) {
        const returnTo = this.desktopMedia.matches
          ? this.querySelector(this.classList.contains('is-collapsed') ? '.catalog-figure-finder__collapsed-trigger' : '.catalog-figure-finder__collapse-toggle')
          : toggle;
        returnTo?.focus();
      }
      this.syncSidebarAccessibility();
      if (open) {
        window.requestAnimationFrame(() => {
          if (this.classList.contains('is-mobile-open')) {
            sidebar.querySelector('[data-cff-mobile-close]')?.focus();
          }
        });
      }
    }

    toggleTag(link, event) {
      const value = this.normalizeTagValue(link.dataset.tagValue);
      if (!value) return;

      const currentUrl = new URL(this.currentUrl?.href || window.location.href);
      const activeValues = this.currentActiveValues
        ? new Set(this.currentActiveValues)
        : new Set(this.activeValues(currentUrl));
      const wasActive = activeValues.has(value);
      const group = link.closest('[data-cff-group]');

      if (group?.dataset.selectionMode === 'single') {
        this.groupValues(group).forEach((groupValue) => activeValues.delete(groupValue));
      } else {
        activeValues.delete(value);
      }

      if (!wasActive) activeValues.add(value);
      this.navigate(this.buildTargetUrl(currentUrl, activeValues), event);
    }

    clearTags(values, event) {
      const currentUrl = new URL(this.currentUrl?.href || window.location.href);
      const activeValues = this.currentActiveValues
        ? new Set(this.currentActiveValues)
        : new Set(this.activeValues(currentUrl));
      values.forEach((value) => activeValues.delete(value));
      this.navigate(this.buildTargetUrl(currentUrl, activeValues), event);
    }

    buildTargetUrl(currentUrl, activeValues) {
      return this.context === 'search'
        ? this.buildSearchUrl(currentUrl, activeValues)
        : this.buildCollectionUrl(currentUrl, activeValues);
    }

    buildCollectionUrl(currentUrl, activeValues) {
      const target = new URL(currentUrl.href);
      const location = this.collectionLocation(currentUrl);
      const configuredHandles = new Set(this.allConfiguredValues().map((value) => this.handleize(value)));
      const retainedUnknownHandles = location.tagHandles.filter((handle) => !configuredHandles.has(handle));
      const selectedHandles = this.allConfiguredValues()
        .filter((value) => activeValues.has(value))
        .map((value) => this.handleize(value));
      const nextHandles = [...new Set([...retainedUnknownHandles, ...selectedHandles])];

      target.pathname = `${location.basePath}${nextHandles.length ? `/${nextHandles.join('+')}` : ''}`;
      target.searchParams.delete('filter.p.tag');
      target.searchParams.delete('ff_tag');
      target.searchParams.delete('ff_q');
      target.searchParams.delete('ff_original_type');
      target.searchParams.delete('page');
      return target;
    }

    buildSearchUrl(currentUrl, activeValues) {
      const target = new URL(currentUrl.href);
      const savedBaseQuery = target.searchParams.get('ff_q');
      const baseQuery =
        savedBaseQuery === null
          ? this.stripConfiguredSearchClauses(target.searchParams.get('q') || '')
          : savedBaseQuery;
      const orderedActiveValues = this.allConfiguredValues().filter((value) => activeValues.has(value));

      target.searchParams.delete('filter.p.tag');
      target.searchParams.delete('ff_tag');
      target.searchParams.delete('page');

      if (orderedActiveValues.length > 0) {
        if (!target.searchParams.has('ff_original_type')) {
          target.searchParams.set('ff_original_type', target.searchParams.get('type') || '__none__');
        }
        target.searchParams.set('ff_q', baseQuery);
        orderedActiveValues.forEach((value) => target.searchParams.append('ff_tag', value));
        const clauses = orderedActiveValues.map((value) => `tag:"${value.replace(/["\\]/g, '')}"`);
        // Quote each tag so a multiword tag remains one Shopify search clause.
        target.searchParams.set('q', [baseQuery, ...clauses].filter(Boolean).join(' ').trim());
        target.searchParams.set('type', 'product');
      } else {
        target.searchParams.set('q', baseQuery);
        const originalType = target.searchParams.get('ff_original_type');
        if (originalType === '__none__' || originalType === null) {
          target.searchParams.delete('type');
        } else {
          target.searchParams.set('type', originalType);
        }
        target.searchParams.delete('ff_q');
        target.searchParams.delete('ff_original_type');
      }

      return target;
    }

    activeValues(url) {
      if (this.context === 'search') {
        const configuredByKey = new Map(
          this.allConfiguredValues().map((value) => [this.tagKey(value), value]),
        );
        const explicitValues = url.searchParams
          .getAll('ff_tag')
          .map((value) => configuredByKey.get(this.tagKey(value)))
          .filter(Boolean);
        if (explicitValues.length > 0) return explicitValues;

        const query = url.searchParams.get('q') || '';
        return this.allConfiguredValues().filter((value) => this.searchClausePattern(value).test(query));
      }

      const location = this.collectionLocation(url);
      const activeHandles = new Set(location.tagHandles);
      url.searchParams.getAll('filter.p.tag').forEach((value) => activeHandles.add(this.handleize(value)));
      return this.allConfiguredValues().filter((value) => activeHandles.has(this.handleize(value)));
    }

    collectionLocation(url) {
      const segments = url.pathname.split('/').filter(Boolean);
      const collectionIndex = segments.lastIndexOf('collections');
      if (collectionIndex === -1 || !segments[collectionIndex + 1]) {
        return { basePath: url.pathname.replace(/\/$/, ''), tagHandles: [] };
      }

      const baseSegments = segments.slice(0, collectionIndex + 2);
      const tagSegment = segments[collectionIndex + 2] || '';
      return {
        basePath: `/${baseSegments.join('/')}`,
        tagHandles: tagSegment
          .split('+')
          .map((handle) => {
            try {
              return decodeURIComponent(handle).toLowerCase();
            } catch (_error) {
              return handle.toLowerCase();
            }
          })
          .filter(Boolean),
      };
    }

    handleize(value) {
      return value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    searchClausePattern(value) {
      const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`(?:^|\\s)tag:(?:"${escaped}"|${escaped})(?=\\s|$)`, 'i');
    }

    stripConfiguredSearchClauses(query) {
      let baseQuery = query;
      this.allConfiguredValues().forEach((value) => {
        baseQuery = baseQuery.replace(new RegExp(this.searchClausePattern(value).source, 'gi'), ' ');
      });
      return baseQuery.replace(/\s+/g, ' ').trim();
    }

    compatibilityData(tag) {
      const key = this.tagKey(tag);
      if (this.compatibilityCache.has(key)) return this.compatibilityCache.get(key);
      const encoded = this.compatibilityKeyMap.get(key);
      if (typeof encoded !== 'string') return null;

      try {
        const binary = window.atob(encoded);
        const bytes = new Uint8Array(binary.length);
        for (let index = 0; index < binary.length; index += 1) {
          bytes[index] = binary.charCodeAt(index);
        }
        this.compatibilityCache.set(key, bytes);
        return bytes;
      } catch (_error) {
        return null;
      }
    }

    compatibilityHasMatch(values) {
      const uniqueValues = [...new Set(values)];
      if (uniqueValues.length === 0) return true;

      const bitsets = uniqueValues.map((value) => this.compatibilityData(value));
      if (bitsets.some((bitset) => !bitset)) return false;

      const byteCount = Math.min(...bitsets.map((bitset) => bitset.length));
      for (let byteIndex = 0; byteIndex < byteCount; byteIndex += 1) {
        let intersection = bitsets[0][byteIndex];
        for (let bitsetIndex = 1; bitsetIndex < bitsets.length; bitsetIndex += 1) {
          intersection &= bitsets[bitsetIndex][byteIndex];
          if (intersection === 0) break;
        }
        if (intersection !== 0) return true;
      }
      return false;
    }

    isTagUnavailable(link, activeValues) {
      const value = this.normalizeTagValue(link.dataset.tagValue);
      if (!value || activeValues.has(value)) return false;

      if (!this.hasCompleteCompatibilityIndex()) return true;

      const requiredValues = new Set(activeValues);
      const group = link.closest('[data-cff-group]');

      if (group?.dataset.selectionMode === 'single') {
        this.groupValues(group).forEach((groupValue) => requiredValues.delete(groupValue));
      }
      requiredValues.add(value);

      return !this.compatibilityHasMatch([...requiredValues]);
    }

    updateCompatibilityAvailability(activeValues) {
      this.querySelectorAll('[data-cff-tag]').forEach((link) => {
        const unavailable = this.isTagUnavailable(link, activeValues);
        link.classList.toggle('is-unavailable', unavailable);
        link.setAttribute('aria-disabled', String(unavailable));

        if (unavailable) {
          link.setAttribute('tabindex', '-1');
          link.setAttribute(
            'title',
            this.hasCompleteCompatibilityIndex()
              ? 'No matching miniatures with the current selections'
              : 'Checking available combinations',
          );
        } else {
          link.removeAttribute('tabindex');
          link.removeAttribute('title');
        }
      });
    }

    announce(message) {
      const status = this.querySelector('[data-cff-status]');
      if (status) status.textContent = message;
    }

    groupValues(group) {
      return [...group.querySelectorAll('[data-cff-tag]')]
        .map((link) => this.normalizeTagValue(link.dataset.tagValue))
        .filter(Boolean);
    }

    allConfiguredValues() {
      return [...this.querySelectorAll('[data-cff-tag]')]
        .map((link) => this.normalizeTagValue(link.dataset.tagValue))
        .filter(Boolean);
    }

    navigate(targetUrl, event) {
      this.classList.add('is-loading');
      this.syncFromUrl(targetUrl);

      this.renderTargetPath(targetUrl, event);
    }

    renderTargetPath(targetUrl, event = null) {
      const sectionId = document.getElementById('product-grid')?.dataset.id;
      if (!sectionId || typeof FacetFiltersForm === 'undefined') {
        window.location.assign(targetUrl.href);
        return;
      }

      // Use the same renderer for tag paths, query filters, sorting, and history.
      FacetFiltersForm.renderPage(targetUrl.search.slice(1), event, true, targetUrl);
    }

    syncFromUrl(url) {
      const activeValues = new Set(this.activeValues(url));
      this.currentUrl = new URL(url.href);
      this.currentActiveValues = new Set(activeValues);
      let activeCount = 0;

      this.querySelectorAll('[data-cff-tag]').forEach((link) => {
        const active = activeValues.has(this.normalizeTagValue(link.dataset.tagValue));
        link.classList.toggle('is-active', active);
        link.setAttribute('role', 'button');
        link.setAttribute('aria-pressed', String(active));
        if (active) activeCount += 1;
      });

      this.querySelectorAll('[data-cff-group]').forEach((group) => {
        const groupCount = this.groupValues(group).filter((value) => activeValues.has(value)).length;
        const countBadge = group.querySelector('[data-cff-group-count]');
        const clearButton = group.querySelector('[data-cff-clear-group]');

        if (countBadge) {
          countBadge.textContent = String(groupCount);
          countBadge.hidden = groupCount === 0;
        }
        if (clearButton) clearButton.hidden = groupCount === 0;
        if (groupCount > 0) group.open = true;
      });

      this.querySelectorAll('[data-cff-active-count]').forEach((badge) => {
        badge.textContent = String(activeCount);
        badge.hidden = activeCount === 0;
      });

      const clearAll = this.querySelector('[data-cff-clear-all]');
      if (clearAll) clearAll.hidden = activeCount === 0;

      this.updateCompatibilityAvailability(activeValues);

      if (this.context === 'search') {
        const searchInput = document.getElementById('Search-In-Template');
        if (searchInput) {
          searchInput.value =
            url.searchParams.get('ff_q') ??
            this.stripConfiguredSearchClauses(url.searchParams.get('q') || '');
        }
      }
    }
  }

  customElements.define('catalog-figure-finder', CatalogFigureFinder);
})();
